import { FacilityState } from "./types";
import { GetFacilityResponse } from "@api/requests/facility/getFacility";
import { PostFacilityParams } from "@api/requests/facility/postFacility";
import { FacilityValues } from "@initialize/mgr/SEIKATSUKAIGO/facility/initialValues";
import {
  INT_FALSE_FROM_API,
  INT_TRUE_FROM_API,
  STRING_FALSE_FROM_API,
  STRING_TRUE_FROM_API,
  DEFAULT_RADIO_VALUE,
  ENABLE_SPECIFIC_BETTER_SUPPORTER_CONDITION_VALUES,
  SELECTED_CONDITION_TYPE_2024_V
} from "@constants/variables";
import {
  selectDateValueToDate,
  selectDateValueToDatePaddingZero
} from "@utils/date";
import {
  OpenShortTime,
  SUPPORT_IKOU_RESULT_ITEMS,
  WELFARE_SPECAILLIST_PLACEMENT_LIST,
  WELFARE_CONDITION_COMBINED_LIST
} from "@constants/mgr/SEIKATSUKAIGO/variables";
import castString from "@utils/dataNormalizer/castString";
import castNumber from "@utils/dataNormalizer/castNumber";
import stringToNumber from "@utils/dataNormalizer/stringToNumber";

// , formatTime
/**
 * Normalized Type
 */
export type NormalizedGetFacilityResponse = FacilityState;
export type NormalizedPostFacilityParams = FacilityState;

/**
 * GetFacilityResponse === PostFacilityParams前提の共通化
 */
const normalizeApiParams = (
  result: GetFacilityResponse["data"] | PostFacilityParams
): FacilityState => {
  const { facility } = result;
  const facilitySEIKATSUKAIGO = result.facility_seikatsukaigo || {
    facility_type: 0,
    doctor_placement_flg: false,
    open_short_time: 0,
    nursing_supporter_202404: 0,
    serious_disability: 0,
    satellite_type_etc_flg: 0,
    serious_supporter_flg: false,
    addition_placement_of_service_admin_flg: false,
    addition_placement_start_date: null,
    addition_placement_end_date: null,
    welfare_condition_combined: null
  };
  let isWelfareSpecialistPlacementType3 = false;
  if (
    facilitySEIKATSUKAIGO.welfare_condition_combined ||
    facility.welfare_condition === WELFARE_SPECAILLIST_PLACEMENT_LIST[3].value
  ) {
    isWelfareSpecialistPlacementType3 = true;
  }

  return {
    corporationName: facility.gov_business_owner,
    officeNumber: facility.gov_facility_number
      ? facility.gov_facility_number
      : "",
    officeName: facility.name ? facility.name : "",
    serviceType: facility.type_service,
    facilityType: facilitySEIKATSUKAIGO.facility_type
      ? facilitySEIKATSUKAIGO.facility_type
      : 0,
    representativeName: facility.responsible_person
      ? facility.responsible_person
      : "",
    capacity: facility.capacity !== null ? facility.capacity.toString() : "",
    postalCode: facility.postal_code !== null ? facility.postal_code : "",
    selectedPrefectureName: facility.prefecture_name
      ? facility.prefecture_name
      : "NOT_SELECTED",
    selectedCityCode:
      facility.city_id !== null ? facility.city_id.toString() : "NOT_SELECTED",
    restAddress: facility.address ? facility.address : "",
    tel: facility.tel ? facility.tel : "",
    cityId: facility.city_id !== null ? facility.city_id.toString() : "",
    multiFunctionOfficeFlag:
      facility.multiple_facility_flg === STRING_TRUE_FROM_API,
    masterSubordinateFlg:
      facility.master_subordinate_flg === STRING_TRUE_FROM_API,
    masterFlg: facility.master_flg ? facility.master_flg : "1",
    allCapacity: castString(facility.total_capacity),
    mealSaservedServiceFlag: facility.available_food === STRING_TRUE_FROM_API,
    transferServiceFlag: facility.available_pickup === STRING_TRUE_FROM_API,
    transferServiceType: facility.available_pickup_kind
      ? facility.available_pickup_kind
      : "1",
    seriousSupporterFlg:
      facilitySEIKATSUKAIGO.serious_supporter_flg === INT_TRUE_FROM_API,
    doctorInstallationFlag:
      facilitySEIKATSUKAIGO.doctor_placement_flg === INT_FALSE_FROM_API,
    mondaySchedule: facility.mon_active_flg === STRING_TRUE_FROM_API,
    mondayStartTime: facility.mon_open
      ? facility.mon_open.toString().substring(0, 5)
      : "",
    mondayEndTime: facility.mon_close
      ? facility.mon_close.toString().substring(0, 5)
      : "",
    tuesdaySchedule: facility.tue_active_flg === STRING_TRUE_FROM_API,
    tuesdayStartTime: facility.tue_open
      ? facility.tue_open.toString().substring(0, 5)
      : "",
    tuesdayEndTime: facility.tue_close
      ? facility.tue_close.toString().substring(0, 5)
      : "",
    wednesdaySchedule: facility.wed_active_flg === STRING_TRUE_FROM_API,
    wednesdayStartTime: facility.wed_open
      ? facility.wed_open.toString().substring(0, 5)
      : "",
    wednesdayEndTime: facility.wed_close
      ? facility.wed_close.toString().substring(0, 5)
      : "",
    thursdaySchedule: facility.thu_active_flg === STRING_TRUE_FROM_API,
    thursdayStartTime: facility.thu_open
      ? facility.thu_open.toString().substring(0, 5)
      : "",
    thursdayEndTime: facility.thu_close
      ? facility.thu_close.toString().substring(0, 5)
      : "",
    fridaySchedule: facility.fri_active_flg === STRING_TRUE_FROM_API,
    fridayStartTime: facility.fri_open
      ? facility.fri_open.toString().substring(0, 5)
      : "",
    fridayEndTime: facility.fri_close
      ? facility.fri_close.toString().substring(0, 5)
      : "",
    saturdaySchedule: facility.sat_active_flg === STRING_TRUE_FROM_API,
    saturdayStartTime: facility.sat_open
      ? facility.sat_open.toString().substring(0, 5)
      : "",
    saturdayEndTime: facility.sat_close
      ? facility.sat_close.toString().substring(0, 5)
      : "",
    sundaySchedule: facility.sun_active_flg === STRING_TRUE_FROM_API,
    sundayStartTime: facility.sun_open
      ? facility.sun_open.toString().substring(0, 5)
      : "",
    sundayEndTime: facility.sun_close
      ? facility.sun_close.toString().substring(0, 5)
      : "",
    openedOrShortTimeFlag:
      facilitySEIKATSUKAIGO.open_short_time !== INT_FALSE_FROM_API,
    lackOfLifeSupportMemberFlag:
      facility.lack_of_supporter_flg === STRING_TRUE_FROM_API,
    lackOfLifeSupportMemberStartDate:
      facility.date_start_lack_of_supporter !== null
        ? facility.date_start_lack_of_supporter
        : "",
    lackOfResponsiblePersonFlag:
      facility.lack_of_service_admin_flg === STRING_TRUE_FROM_API,
    lackOfResponsiblePersonStartDate:
      facility.date_start_lack_of_service_admin !== null
        ? facility.date_start_lack_of_service_admin
        : "",
    seeHearTeam202404: facility.see_hear_team_202404
      ? facility.see_hear_team_202404.toString()
      : DEFAULT_RADIO_VALUE,
    seeHearTeam202404From: facility.see_hear_team_202404_from
      ? facility.see_hear_team_202404_from
      : "",
    seeHearTeam202404To: facility.see_hear_team_202404_to
      ? facility.see_hear_team_202404_to
      : "",
    higherBrainDysfunctionPersonsSupportFlg:
      facility.higher_brain_dysfunction_persons_support_flg ===
      INT_TRUE_FROM_API,
    higherBrainDysfunctionPersonsSupportFrom: facility.higher_brain_dysfunction_persons_support_from
      ? facility.higher_brain_dysfunction_persons_support_from
      : "",
    higherBrainDysfunctionPersonsSupportTo: facility.higher_brain_dysfunction_persons_support_to
      ? facility.higher_brain_dysfunction_persons_support_to
      : "",
    bodyRestrictedStillFlg:
      facility.body_restricted_still_flg === INT_TRUE_FROM_API,
    bodyRestrictedStillFrom: facility.body_restricted_still_from
      ? facility.body_restricted_still_from
      : "",
    bodyRestrictedStillTo: facility.body_restricted_still_to
      ? facility.body_restricted_still_to
      : "",
    abusePreventionMeasuresNotImplementedFlg:
      facility.abuse_prevention_measures_not_implemented_flg ===
      INT_TRUE_FROM_API,
    abusePreventionMeasuresNotImplementedFrom: facility.abuse_prevention_measures_not_implemented_from
      ? facility.abuse_prevention_measures_not_implemented_from
      : "",
    abusePreventionMeasuresNotImplementedTo: facility.abuse_prevention_measures_not_implemented_to
      ? facility.abuse_prevention_measures_not_implemented_to
      : "",
    workContinuationNotPlanningFlg:
      facility.work_continuation_not_planning_flg === INT_TRUE_FROM_API,
    workContinuationNotPlanningFrom: facility.work_continuation_not_planning_from
      ? facility.work_continuation_not_planning_from
      : "",
    workContinuationNotPlanningTo: facility.work_continuation_not_planning_to
      ? facility.work_continuation_not_planning_to
      : "",
    informationDisclosureNotReportedFlg:
      facility.information_disclosure_not_reported_flg === INT_TRUE_FROM_API,
    informationDisclosureNotReportedFrom: facility.information_disclosure_not_reported_from
      ? facility.information_disclosure_not_reported_from
      : "",
    informationDisclosureNotReportedTo: facility.information_disclosure_not_reported_to
      ? facility.information_disclosure_not_reported_to
      : "",
    establishedByLocalGovernmentsFlag:
      facility.origin_local_gov_flg === STRING_TRUE_FROM_API,
    welfareSpecialistPlacementType: facility.welfare_condition
      ? facility.welfare_condition
      : DEFAULT_RADIO_VALUE,
    welfareConditionStartDate: facility.welfare_condition_start_date || "",
    welfareConditionEndDate: facility.welfare_condition_end_date || "",
    nursingSupporterFlg: facilitySEIKATSUKAIGO.nursing_supporter_202404
      ? facilitySEIKATSUKAIGO.nursing_supporter_202404 > 0
      : false,
    nursingSupporter:
      facilitySEIKATSUKAIGO.nursing_supporter_202404 &&
      facilitySEIKATSUKAIGO.nursing_supporter_202404 > 0
        ? facilitySEIKATSUKAIGO.nursing_supporter_202404.toString()
        : "",
    specificBetterSupporterCondition202404: facility.specific_better_supporter_condition_202404
      ? facility.specific_better_supporter_condition_202404
      : DEFAULT_RADIO_VALUE,
    staffTreatmentSystemType: facility.better_supporter_condition
      ? facility.better_supporter_condition
      : DEFAULT_RADIO_VALUE,
    staffTreatmentSpecificSystemType: facility.specific_better_supporter_condition
      ? facility.specific_better_supporter_condition
      : DEFAULT_RADIO_VALUE,
    commuterLifeSupportFlag:
      facility.execute_kaizen_flg === STRING_TRUE_FROM_API,
    betterSupporterConditionBaseUpFlg:
      facility.better_supporter_condition_base_up_flg === STRING_TRUE_FROM_API,
    seriousDisability: facilitySEIKATSUKAIGO.serious_disability
      ? facilitySEIKATSUKAIGO.serious_disability.toString()
      : "0",
    visualAuditoryLanguageDisabledPeopleSupportSystemFlag: facility.see_hear_team_flg
      ? facility.see_hear_team_flg === STRING_TRUE_FROM_API
      : false,
    employmentTransitionSupportFlag:
      facility.ab_support_ikou_flg === STRING_TRUE_FROM_API &&
      facility.ab_support_ikou_result === SUPPORT_IKOU_RESULT_ITEMS[1].value,
    numberOfContinuations: castString(facility.ab_support_ikou_result_number),
    staffPlacementType: facility.users_vs_supporter_grade_202404
      ? facility.users_vs_supporter_grade_202404.toString()
      : "0",
    placementOfServiceAdminFlag: !!facilitySEIKATSUKAIGO.addition_placement_of_service_admin_flg,
    placementStartDate:
      facilitySEIKATSUKAIGO.addition_placement_start_date || "",
    placementEndDate: facilitySEIKATSUKAIGO.addition_placement_end_date || "",
    welfareConditionCombined: facilitySEIKATSUKAIGO.welfare_condition_combined
      ? facilitySEIKATSUKAIGO.welfare_condition_combined.toString()
      : "",
    welfareSpecialistPlacementType1:
      facilitySEIKATSUKAIGO.welfare_condition_combined ===
        WELFARE_CONDITION_COMBINED_LIST[0].value ||
      facility.welfare_condition ===
        WELFARE_SPECAILLIST_PLACEMENT_LIST[1].value,
    welfareSpecialistPlacementType2:
      facilitySEIKATSUKAIGO.welfare_condition_combined ===
        WELFARE_CONDITION_COMBINED_LIST[1].value ||
      facility.welfare_condition ===
        WELFARE_SPECAILLIST_PLACEMENT_LIST[2].value,
    welfareSpecialistPlacementType3: isWelfareSpecialistPlacementType3
  };
};

/**
 * フラグがOFFの時、関連する値を消す
 * 入力後にdisabledとなったテキストエリアのテキストを消す
 */
const removeUnnecessaryValue = (
  target: PostFacilityParams
): PostFacilityParams => {
  const result = {
    facility: { ...target.facility },
    facility_seikatsukaigo: target.facility_seikatsukaigo
      ? { ...target.facility_seikatsukaigo }
      : undefined
  };
  if (result.facility.lack_of_service_admin_flg === STRING_FALSE_FROM_API) {
    result.facility.date_start_lack_of_service_admin = null;
  }
  if (result.facility.lack_of_supporter_flg === STRING_FALSE_FROM_API) {
    result.facility.date_start_lack_of_supporter = null;
  }
  if (
    result.facility.ab_support_ikou_result !==
    SUPPORT_IKOU_RESULT_ITEMS[1].value
  ) {
    result.facility.ab_support_ikou_result_number = null;
  }
  if (
    !ENABLE_SPECIFIC_BETTER_SUPPORTER_CONDITION_VALUES.includes(
      result.facility.better_supporter_condition
    )
  ) {
    result.facility.specific_better_supporter_condition = DEFAULT_RADIO_VALUE;
  }
  if (
    result.facility.available_pickup === STRING_FALSE_FROM_API &&
    result.facility_seikatsukaigo
  ) {
    result.facility_seikatsukaigo.serious_supporter_flg = INT_FALSE_FROM_API;
  }
  // 福祉専門職員配置等加算: なしの時
  if (result.facility.welfare_condition === "1") {
    result.facility.welfare_condition_start_date = null;
    result.facility.welfare_condition_end_date = null;
  }
  return result;
};

// パラメータの親子関係マッピング表
const parentParamsMap = {
  facility: {
    ab_support_ikou_flg: {
      key: "ab_support_ikou_flg",
      childKeys: ["ab_support_ikou_result", "ab_support_ikou_result_number"]
    },
    ab_support_ikou_result: {
      key: "ab_support_ikou_result",
      childKeys: ["ab_support_ikou_result_number"]
    },
    available_pickup: {
      key: "available_pickup",
      childKeys: ["available_pickup_kind", "serious_supporter_flg"]
    },
    mon_active_flg: {
      key: "mon_active_flg",
      childKeys: ["mon_open", "mon_close"]
    },
    mon_open: {
      key: "mon_open",
      childKeys: ["mon_close"]
    },
    mon_close: {
      key: "mon_close",
      childKeys: ["mon_open"]
    },
    tue_active_flg: {
      key: "tue_active_flg",
      childKeys: ["tue_open", "tue_close"]
    },
    tue_open: {
      key: "tue_open",
      childKeys: ["tue_close"]
    },
    tue_close: {
      key: "tue_close",
      childKeys: ["tue_open"]
    },
    wed_active_flg: {
      key: "wed_active_flg",
      childKeys: ["wed_open", "wed_close"]
    },
    wed_open: {
      key: "wed_open",
      childKeys: ["wed_close"]
    },
    wed_close: {
      key: "wed_close",
      childKeys: ["wed_open"]
    },
    thu_active_flg: {
      key: "thu_active_flg",
      childKeys: ["thu_open", "thu_close"]
    },
    thu_open: {
      key: "thu_open",
      childKeys: ["thu_close"]
    },
    thu_close: {
      key: "thu_close",
      childKeys: ["thu_open"]
    },
    fri_active_flg: {
      key: "fri_active_flg",
      childKeys: ["fri_open", "fri_close"]
    },
    fri_open: {
      key: "fri_open",
      childKeys: ["fri_close"]
    },
    fri_close: {
      key: "fri_close",
      childKeys: ["fri_open"]
    },
    sat_active_flg: {
      key: "sat_active_flg",
      childKeys: ["sat_open", "sat_close"]
    },
    sat_open: {
      key: "sat_open",
      childKeys: ["sat_close"]
    },
    sat_close: {
      key: "sat_close",
      childKeys: ["sat_open"]
    },
    sun_active_flg: {
      key: "sun_active_flg",
      childKeys: ["sun_open", "sun_close"]
    },
    sun_open: {
      key: "sun_open",
      childKeys: ["sun_close"]
    },
    sun_close: {
      key: "sun_close",
      childKeys: ["sun_open"]
    },
    better_supporter_condition: {
      key: "better_supporter_condition",
      childKeys: ["specific_better_supporter_condition"]
    }
  },
  facility_seikatsukaigo: {
    nursing_supporter_flg: {
      key: "nursing_supporter_flg",
      childKeys: ["nursing_supporter_202404"]
    }
  }
};

/**
 * 子要素の差分判定 子要素に差分がある場合、親要素もパラメータに付与する
 * @param target
 * @param after
 */
const addParentValue = (
  target: PostFacilityParams,
  after: PostFacilityParams
): PostFacilityParams => {
  const result = target;
  Object.keys(parentParamsMap).forEach((facilityKey) => {
    Object.keys(parentParamsMap[facilityKey]).forEach((paramKey) => {
      const childKeys = parentParamsMap[facilityKey][paramKey].childKeys
        ? parentParamsMap[facilityKey][paramKey].childKeys
        : [];
      const isDiffChildren: boolean = childKeys.some((childKey: string) => {
        return result[facilityKey][childKey] !== undefined;
      });
      if (isDiffChildren) {
        result[facilityKey][parentParamsMap[facilityKey][paramKey].key] =
          after[facilityKey][parentParamsMap[facilityKey][paramKey].key];
      }
    });
  });
  return result;
};

export const normalizeGetFacilityResult = (
  response: GetFacilityResponse
): NormalizedGetFacilityResponse => normalizeApiParams(response.data);

export const normalizePostFacilityParams = (
  params: PostFacilityParams
): NormalizedPostFacilityParams => normalizeApiParams(params);

export const removeNoChangeData = (
  after: PostFacilityParams,
  before: PostFacilityParams
): PostFacilityParams => {
  const target: PostFacilityParams = {
    facility: {
      gov_business_owner: after.facility.gov_business_owner,
      gov_facility_number: after.facility.gov_facility_number,
      name: after.facility.name,
      type_service: after.facility.type_service,
      responsible_person: after.facility.responsible_person,
      capacity: after.facility.capacity,
      postal_code: after.facility.postal_code,
      prefecture_name: after.facility.prefecture_name,
      city_id: after.facility.city_id,
      address: after.facility.address,
      tel: after.facility.tel,
      multiple_facility_flg: after.facility.multiple_facility_flg,
      users_vs_supporter_grade_202404:
        after.facility.users_vs_supporter_grade_202404,
      welfare_condition: after.facility.welfare_condition,
      welfare_condition_start_date: after.facility.welfare_condition_start_date,
      welfare_condition_end_date: after.facility.welfare_condition_end_date,
      better_supporter_condition: after.facility.better_supporter_condition,
      lack_of_supporter_flg: after.facility.lack_of_supporter_flg,
      date_start_lack_of_supporter: after.facility.date_start_lack_of_supporter,
      lack_of_service_admin_flg: after.facility.lack_of_service_admin_flg,
      date_start_lack_of_service_admin:
        after.facility.date_start_lack_of_service_admin,
      see_hear_team_202404: undefined,
      see_hear_team_202404_from: undefined,
      see_hear_team_202404_to: undefined,
      higher_brain_dysfunction_persons_support_flg: undefined,
      higher_brain_dysfunction_persons_support_from: undefined,
      higher_brain_dysfunction_persons_support_to: undefined,
      body_restricted_still_flg: undefined,
      body_restricted_still_from: undefined,
      body_restricted_still_to: undefined,
      abuse_prevention_measures_not_implemented_flg: undefined,
      abuse_prevention_measures_not_implemented_from: undefined,
      abuse_prevention_measures_not_implemented_to: undefined,
      work_continuation_not_planning_flg: undefined,
      work_continuation_not_planning_from: undefined,
      work_continuation_not_planning_to: undefined,
      information_disclosure_not_reported_flg: undefined,
      information_disclosure_not_reported_from: undefined,
      information_disclosure_not_reported_to: undefined,
      origin_local_gov_flg: after.facility.origin_local_gov_flg,
      see_hear_team_flg: after.facility.see_hear_team_flg,
      total_capacity: after.facility.total_capacity,
      available_food: undefined,
      available_pickup: undefined,
      available_pickup_kind: undefined,
      mon_active_flg: undefined,
      tue_active_flg: undefined,
      wed_active_flg: undefined,
      thu_active_flg: undefined,
      fri_active_flg: undefined,
      sat_active_flg: undefined,
      sun_active_flg: undefined,
      mon_open: undefined,
      tue_open: undefined,
      wed_open: undefined,
      thu_open: undefined,
      fri_open: undefined,
      sat_open: undefined,
      sun_open: undefined,
      mon_close: undefined,
      tue_close: undefined,
      wed_close: undefined,
      thu_close: undefined,
      fri_close: undefined,
      sat_close: undefined,
      sun_close: undefined,
      execute_kaizen_flg: undefined,
      better_supporter_condition_base_up_flg: undefined,
      specific_better_supporter_condition_202404: undefined,
      specific_better_supporter_condition: undefined,
      ab_support_ikou_flg: undefined,
      ab_support_ikou_result: undefined,
      ab_support_ikou_result_number: undefined
    },
    facility_seikatsukaigo: {
      facility_type: undefined,
      satellite_type_etc_flg: undefined,
      doctor_placement_flg: undefined,
      open_short_time: undefined,
      nursing_supporter_flg: undefined,
      nursing_supporter_202404: undefined,
      serious_disability: undefined,
      serious_supporter_flg: undefined,
      addition_placement_of_service_admin_flg: undefined,
      addition_placement_start_date: after.facility_seikatsukaigo
        ? after.facility_seikatsukaigo.addition_placement_start_date
        : undefined,
      addition_placement_end_date: after.facility_seikatsukaigo
        ? after.facility_seikatsukaigo.addition_placement_end_date
        : undefined,
      welfare_condition_combined: after.facility_seikatsukaigo
        ? after.facility_seikatsukaigo.welfare_condition_combined
        : undefined
    }
  };

  Object.keys(target).forEach((key) => {
    Object.keys(target[key]).forEach((param) => {
      if (target[key][param] === undefined) {
        if (`${before[key][param]}` !== `${after[key][param]}`) {
          target[key][param] = after[key][param];
        } else {
          target[key][param] = undefined;
        }
      }
    });
  });

  // 身体拘束廃止未実施減算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.body_restricted_still_flg !== undefined ||
    target.facility.body_restricted_still_from !== undefined ||
    target.facility.body_restricted_still_to !== undefined
  ) {
    target.facility.body_restricted_still_flg =
      after.facility.body_restricted_still_flg;
    target.facility.body_restricted_still_from =
      after.facility.body_restricted_still_from;
    target.facility.body_restricted_still_to =
      after.facility.body_restricted_still_to;
  }

  // 虐待防止措置未実施減算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.abuse_prevention_measures_not_implemented_flg !==
      undefined ||
    target.facility.abuse_prevention_measures_not_implemented_from !==
      undefined ||
    target.facility.abuse_prevention_measures_not_implemented_to !== undefined
  ) {
    target.facility.abuse_prevention_measures_not_implemented_flg =
      after.facility.abuse_prevention_measures_not_implemented_flg;
    target.facility.abuse_prevention_measures_not_implemented_from =
      after.facility.abuse_prevention_measures_not_implemented_from;
    target.facility.abuse_prevention_measures_not_implemented_to =
      after.facility.abuse_prevention_measures_not_implemented_to;
  }

  // 業務継続計画未策定減算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.work_continuation_not_planning_flg !== undefined ||
    target.facility.work_continuation_not_planning_from !== undefined ||
    target.facility.work_continuation_not_planning_to !== undefined
  ) {
    target.facility.work_continuation_not_planning_flg =
      after.facility.work_continuation_not_planning_flg;
    target.facility.work_continuation_not_planning_from =
      after.facility.work_continuation_not_planning_from;
    target.facility.work_continuation_not_planning_to =
      after.facility.work_continuation_not_planning_to;
  }

  // 情報公表未報告減算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.information_disclosure_not_reported_flg !== undefined ||
    target.facility.information_disclosure_not_reported_from !== undefined ||
    target.facility.information_disclosure_not_reported_to !== undefined
  ) {
    target.facility.information_disclosure_not_reported_flg =
      after.facility.information_disclosure_not_reported_flg;
    target.facility.information_disclosure_not_reported_from =
      after.facility.information_disclosure_not_reported_from;
    target.facility.information_disclosure_not_reported_to =
      after.facility.information_disclosure_not_reported_to;
  }

  // 視覚・聴覚障害者支援体制加算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.see_hear_team_202404 !== undefined ||
    target.facility.see_hear_team_202404_from !== undefined ||
    target.facility.see_hear_team_202404_to !== undefined
  ) {
    target.facility.see_hear_team_202404 = after.facility.see_hear_team_202404;
    target.facility.see_hear_team_202404_from =
      after.facility.see_hear_team_202404_from;
    target.facility.see_hear_team_202404_to =
      after.facility.see_hear_team_202404_to;
  }

  // 高次脳機能障害者支援体制加算のフラグ、開始終了年月日はセットで送信する
  if (
    target.facility.higher_brain_dysfunction_persons_support_flg !==
      undefined ||
    target.facility.higher_brain_dysfunction_persons_support_from !==
      undefined ||
    target.facility.higher_brain_dysfunction_persons_support_to !== undefined
  ) {
    target.facility.higher_brain_dysfunction_persons_support_flg =
      after.facility.higher_brain_dysfunction_persons_support_flg;
    target.facility.higher_brain_dysfunction_persons_support_from =
      after.facility.higher_brain_dysfunction_persons_support_from;
    target.facility.higher_brain_dysfunction_persons_support_to =
      after.facility.higher_brain_dysfunction_persons_support_to;
  }
  return addParentValue(target, after);
};

export const normalizeFormValue = (
  state: FacilityValues
): PostFacilityParams => {
  let welfareCondition = WELFARE_SPECAILLIST_PLACEMENT_LIST[0].value;
  if (
    state.additionalItem.welfareSpecialistPlacementType1 &&
    !state.additionalItem.welfareSpecialistPlacementType3
  ) {
    welfareCondition = WELFARE_SPECAILLIST_PLACEMENT_LIST[1].value;
  } else if (
    state.additionalItem.welfareSpecialistPlacementType2 &&
    !state.additionalItem.welfareSpecialistPlacementType3
  ) {
    welfareCondition = WELFARE_SPECAILLIST_PLACEMENT_LIST[2].value;
  } else if (
    state.additionalItem.welfareSpecialistPlacementType3 &&
    !state.additionalItem.welfareSpecialistPlacementType1 &&
    !state.additionalItem.welfareSpecialistPlacementType2
  ) {
    welfareCondition = WELFARE_SPECAILLIST_PLACEMENT_LIST[3].value;
  }
  let welfareConditionCombined = null;
  if (
    state.additionalItem.welfareSpecialistPlacementType1 &&
    state.additionalItem.welfareSpecialistPlacementType3
  ) {
    welfareConditionCombined = WELFARE_CONDITION_COMBINED_LIST[0].value;
  } else if (
    state.additionalItem.welfareSpecialistPlacementType2 &&
    state.additionalItem.welfareSpecialistPlacementType3
  ) {
    welfareConditionCombined = WELFARE_CONDITION_COMBINED_LIST[1].value;
  }
  return removeUnnecessaryValue({
    facility: {
      gov_business_owner: state.basic.corporationName,
      gov_facility_number: state.basic.officeNumber,
      name: state.basic.officeName,
      type_service: state.basic.serviceType,
      responsible_person: state.basic.representativeName,
      capacity:
        state.basic.capacity !== "" ? parseInt(state.basic.capacity, 10) : null,
      postal_code: state.basic.postalCode,
      prefecture_name: state.basic.prefectureId,
      city_id: parseInt(state.basic.cityId, 10),
      address: state.basic.restAddress,
      tel: state.basic.tel,
      multiple_facility_flg: state.basic.multiFunctionOfficeFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      users_vs_supporter_grade_202404: state.additionalItem.staffPlacementType,
      welfare_condition: welfareCondition,
      welfare_condition_start_date:
        selectDateValueToDate(state.additionalItem.welfareConditionStartDate) ||
        null,
      welfare_condition_end_date:
        selectDateValueToDate(state.additionalItem.welfareConditionEndDate) ||
        null,
      better_supporter_condition: state.additionalItem.staffTreatmentSystemType,
      lack_of_supporter_flg: state.subtractionItem.lackOfLifeSupportMemberFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      date_start_lack_of_supporter: state.subtractionItem
        .lackOfLifeSupportMemberFlag
        ? selectDateValueToDate(
            state.subtractionItem.lackOfLifeSupportMemberStartDate
          )
        : null,
      lack_of_service_admin_flg: state.subtractionItem
        .lackOfResponsiblePersonFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      date_start_lack_of_service_admin: state.subtractionItem
        .lackOfResponsiblePersonFlag
        ? selectDateValueToDate(
            state.subtractionItem.lackOfResponsiblePersonStartDate
          )
        : null,
      see_hear_team_202404: stringToNumber(
        state.additionalItem.seeHearTeam202404
      ),
      see_hear_team_202404_from:
        state.additionalItem.seeHearTeam202404From &&
        selectDateValueToDate(state.additionalItem.seeHearTeam202404From) !== ""
          ? selectDateValueToDate(state.additionalItem.seeHearTeam202404From)
          : null,
      see_hear_team_202404_to:
        state.additionalItem.seeHearTeam202404To &&
        selectDateValueToDate(state.additionalItem.seeHearTeam202404To) !== ""
          ? selectDateValueToDate(state.additionalItem.seeHearTeam202404To)
          : null,
      higher_brain_dysfunction_persons_support_flg: state.additionalItem
        .higherBrainDysfunctionPersonsSupportFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      higher_brain_dysfunction_persons_support_from:
        state.additionalItem.higherBrainDysfunctionPersonsSupportFrom &&
        selectDateValueToDate(
          state.additionalItem.higherBrainDysfunctionPersonsSupportFrom
        ) !== ""
          ? selectDateValueToDate(
              state.additionalItem.higherBrainDysfunctionPersonsSupportFrom
            )
          : null,
      higher_brain_dysfunction_persons_support_to:
        state.additionalItem.higherBrainDysfunctionPersonsSupportTo &&
        selectDateValueToDate(
          state.additionalItem.higherBrainDysfunctionPersonsSupportTo
        ) !== ""
          ? selectDateValueToDate(
              state.additionalItem.higherBrainDysfunctionPersonsSupportTo
            )
          : null,
      body_restricted_still_flg: state.subtractionItem.bodyRestrictedStillFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      body_restricted_still_from:
        state.subtractionItem.bodyRestrictedStillFrom &&
        selectDateValueToDate(state.subtractionItem.bodyRestrictedStillFrom) !==
          ""
          ? selectDateValueToDate(state.subtractionItem.bodyRestrictedStillFrom)
          : null,
      body_restricted_still_to:
        state.subtractionItem.bodyRestrictedStillTo &&
        selectDateValueToDate(state.subtractionItem.bodyRestrictedStillTo) !==
          ""
          ? selectDateValueToDate(state.subtractionItem.bodyRestrictedStillTo)
          : null,
      abuse_prevention_measures_not_implemented_flg: state.subtractionItem
        .abusePreventionMeasuresNotImplementedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      abuse_prevention_measures_not_implemented_from:
        state.subtractionItem.abusePreventionMeasuresNotImplementedFrom &&
        selectDateValueToDate(
          state.subtractionItem.abusePreventionMeasuresNotImplementedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.abusePreventionMeasuresNotImplementedFrom
            )
          : null,
      abuse_prevention_measures_not_implemented_to:
        state.subtractionItem.abusePreventionMeasuresNotImplementedTo &&
        selectDateValueToDate(
          state.subtractionItem.abusePreventionMeasuresNotImplementedTo
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.abusePreventionMeasuresNotImplementedTo
            )
          : null,
      work_continuation_not_planning_flg: state.subtractionItem
        .workContinuationNotPlanningFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      work_continuation_not_planning_from:
        state.subtractionItem.workContinuationNotPlanningFrom &&
        selectDateValueToDate(
          state.subtractionItem.workContinuationNotPlanningFrom
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.workContinuationNotPlanningFrom
            )
          : null,
      work_continuation_not_planning_to:
        state.subtractionItem.workContinuationNotPlanningTo &&
        selectDateValueToDate(
          state.subtractionItem.workContinuationNotPlanningTo
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.workContinuationNotPlanningTo
            )
          : null,
      information_disclosure_not_reported_flg: state.subtractionItem
        .informationDisclosureNotReportedFlg
        ? INT_TRUE_FROM_API
        : INT_FALSE_FROM_API,
      information_disclosure_not_reported_from:
        state.subtractionItem.informationDisclosureNotReportedFrom &&
        selectDateValueToDate(
          state.subtractionItem.informationDisclosureNotReportedFrom
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.informationDisclosureNotReportedFrom
            )
          : null,
      information_disclosure_not_reported_to:
        state.subtractionItem.informationDisclosureNotReportedTo &&
        selectDateValueToDate(
          state.subtractionItem.informationDisclosureNotReportedTo
        ) !== ""
          ? selectDateValueToDate(
              state.subtractionItem.informationDisclosureNotReportedTo
            )
          : null,
      origin_local_gov_flg: state.subtractionItem
        .establishedByLocalGovernmentsFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      see_hear_team_flg: state.additionalItem
        .visualAuditoryLanguageDisabledPeopleSupportSystemFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,

      total_capacity:
        state.basic.multiFunctionOfficeFlag && state.basic.allCapacity !== ""
          ? parseInt(state.basic.allCapacity, 10)
          : null,
      available_food: state.basic.mealSaservedServiceFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      available_pickup: state.basic.transferServiceFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      available_pickup_kind: state.basic.transferServiceFlag
        ? state.basic.transferServiceType
        : null,

      mon_active_flg: state.administration.mondaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      tue_active_flg: state.administration.tuesdaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      wed_active_flg: state.administration.wednesdaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      thu_active_flg: state.administration.thursdaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      fri_active_flg: state.administration.fridaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      sat_active_flg: state.administration.saturdaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      sun_active_flg: state.administration.sundaySchedule
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      mon_open: state.administration.mondaySchedule
        ? state.administration.mondayStartTime
        : null,
      tue_open: state.administration.tuesdaySchedule
        ? state.administration.tuesdayStartTime
        : null,
      wed_open: state.administration.wednesdaySchedule
        ? state.administration.wednesdayStartTime
        : null,
      thu_open: state.administration.thursdaySchedule
        ? state.administration.thursdayStartTime
        : null,
      fri_open: state.administration.fridaySchedule
        ? state.administration.fridayStartTime
        : null,
      sat_open: state.administration.saturdaySchedule
        ? state.administration.saturdayStartTime
        : null,
      sun_open: state.administration.sundaySchedule
        ? state.administration.sundayStartTime
        : null,
      mon_close: state.administration.mondaySchedule
        ? state.administration.mondayEndTime
        : null,
      tue_close: state.administration.tuesdaySchedule
        ? state.administration.tuesdayEndTime
        : null,
      wed_close: state.administration.wednesdaySchedule
        ? state.administration.wednesdayEndTime
        : null,
      thu_close: state.administration.thursdaySchedule
        ? state.administration.thursdayEndTime
        : null,
      fri_close: state.administration.fridaySchedule
        ? state.administration.fridayEndTime
        : null,
      sat_close: state.administration.saturdaySchedule
        ? state.administration.saturdayEndTime
        : null,
      sun_close: state.administration.sundaySchedule
        ? state.administration.sundayEndTime
        : null,
      execute_kaizen_flg: state.basic.commuterLifeSupportFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      better_supporter_condition_base_up_flg: state.additionalItem
        .betterSupporterConditionBaseUpFlg
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      specific_better_supporter_condition_202404:
        state.additionalItem.specificBetterSupporterCondition202404 ===
          `${SELECTED_CONDITION_TYPE_2024_V}` &&
        state.additionalItem.specificBetterSupporterCondition202404Sub
          ? state.additionalItem.specificBetterSupporterCondition202404Sub
          : state.additionalItem.specificBetterSupporterCondition202404,
      specific_better_supporter_condition:
        state.additionalItem.staffTreatmentSpecificSystemType,
      ab_support_ikou_flg: state.additionalItem.employmentTransitionSupportFlag
        ? STRING_TRUE_FROM_API
        : STRING_FALSE_FROM_API,
      ab_support_ikou_result: state.additionalItem
        .employmentTransitionSupportFlag
        ? SUPPORT_IKOU_RESULT_ITEMS[1].value
        : null,
      ab_support_ikou_result_number:
        state.additionalItem.employmentTransitionSupportFlag &&
        state.additionalItem.numberOfContinuations !== ""
          ? parseInt(state.additionalItem.numberOfContinuations, 10)
          : null
    },
    facility_seikatsukaigo: {
      facility_type:
        state.basic.facilityType !== ""
          ? parseInt(state.basic.facilityType, 10)
          : 0,
      satellite_type_etc_flg: 0,
      doctor_placement_flg: state.subtractionItem.doctorInstallationFlag
        ? INT_FALSE_FROM_API
        : INT_TRUE_FROM_API,
      open_short_time: state.subtractionItem.openedOrShortTimeFlag
        ? parseInt(OpenShortTime.MORE_SIX, 10)
        : parseInt(OpenShortTime.NONE, 10),
      nursing_supporter_flg: state.additionalItem.nursingSupporterFlg,
      nursing_supporter_202404: state.additionalItem.nursingSupporterFlg
        ? parseInt(state.additionalItem.nursingSupporter, 10)
        : 0,
      serious_disability: castNumber(state.additionalItem.seriousDisability),
      serious_supporter_flg:
        state.basic.transferServiceFlag && state.basic.seriousSupporterFlg
          ? INT_TRUE_FROM_API
          : INT_FALSE_FROM_API,
      addition_placement_of_service_admin_flg: Number(
        state.additionalItem.placementOfServiceAdminFlag
      ),
      addition_placement_start_date:
        selectDateValueToDatePaddingZero(
          state.additionalItem.placementStartDate
        ) || null,
      addition_placement_end_date:
        selectDateValueToDatePaddingZero(
          state.additionalItem.placementEndDate
        ) || null,
      welfare_condition_combined: welfareConditionCombined
    }
  });
};

export const normalizePostDifferenceParams = (
  params: PostFacilityParams,
  response: FacilityValues
): PostFacilityParams => {
  return removeNoChangeData(params, normalizeFormValue(response));
};
